/**
 * useIntegrityVotes — хук для голосования за замороженные вкладки (Вариант A).
 *
 * Стратегия хранения:
 * - Голоса загружаются с сервера (GET /projects/{id}/integrity/votes).
 * - При голосовании — optimistic update в localStorage + POST на сервер.
 * - Если сервер недоступен — голос сохраняется только локально.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '@/api/client';

export type VoteValue = 'critical' | 'nice' | 'not_needed';

export interface ViewVoteCounts {
  critical: number;
  nice: number;
  not_needed: number;
  user_vote?: string | null;
}

interface VotesResponse {
  votes: Record<string, ViewVoteCounts>;
}

const LS_PREFIX = 'integrity_vote_';

function lsKey(projectId: string, viewId: string) {
  return `${LS_PREFIX}${projectId}_${viewId}`;
}

function loadLocalVote(projectId: string, viewId: string): VoteValue | null {
  try {
    const raw = localStorage.getItem(lsKey(projectId, viewId));
    return raw as VoteValue | null;
  } catch {
    return null;
  }
}

function saveLocalVote(projectId: string, viewId: string, value: VoteValue) {
  try {
    localStorage.setItem(lsKey(projectId, viewId), value);
  } catch {
    // ignore storage errors
  }
}

export function useIntegrityVotes(projectId: string) {
  const [votes, setVotes] = useState<Record<string, ViewVoteCounts>>({});
  const [loading, setLoading] = useState(false);

  // Загружаем с сервера при монтировании
  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api
      .get<VotesResponse>(`/projects/${projectId}/integrity/votes`)
      .then((data) => {
        setVotes(data.votes ?? {});
      })
      .catch(() => {
        // Если сервер недоступен — используем только localStorage
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  // Переопределяем user_vote из localStorage (актуально для анонимов)
  useEffect(() => {
    if (!projectId) return;
    setVotes((prev) => {
      const next = { ...prev };
      for (const viewId of Object.keys(next)) {
        const local = loadLocalVote(projectId, viewId);
        if (local && !next[viewId].user_vote) {
          next[viewId] = { ...next[viewId], user_vote: local };
        }
      }
      return next;
    });
  }, [projectId]);

  const castVote = useCallback(
    async (viewId: string, value: VoteValue) => {
      // Optimistic update
      saveLocalVote(projectId, viewId, value);
      setVotes((prev) => {
        const cur = prev[viewId] ?? { critical: 0, nice: 0, not_needed: 0 };
        const oldVote = cur.user_vote as VoteValue | undefined;

        const next = { ...cur, user_vote: value };
        // Adjust counters: убираем старый голос, добавляем новый
        if (oldVote && oldVote !== value) {
          if (oldVote === 'critical') next.critical = Math.max(0, next.critical - 1);
          if (oldVote === 'nice') next.nice = Math.max(0, next.nice - 1);
          if (oldVote === 'not_needed') next.not_needed = Math.max(0, next.not_needed - 1);
        }
        if (!oldVote || oldVote !== value) {
          if (value === 'critical') next.critical += 1;
          if (value === 'nice') next.nice += 1;
          if (value === 'not_needed') next.not_needed += 1;
        }
        return { ...prev, [viewId]: next };
      });

      // Синхронизируем с сервером (fire-and-forget; ошибка не критична)
      api
        .post(`/projects/${projectId}/integrity/votes`, { view_id: viewId, value })
        .catch(() => {/* локальный голос уже сохранён */});
    },
    [projectId],
  );

  return { votes, loading, castVote };
}
